import { useState, useCallback, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import TaskService from '../../../../services/task_service';
import EmployeeService from '../../../../services/employee_service';
import { TenantContext } from '../../../../contexts/TenantContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { TASK_STATUS, TASK_STATUS_LABELS, TASK_TYPE } from '../../../../constants/tasks';
import { isFutureDate, formatTaskDate } from '../../../../utils/date';

export const useOperationalKanban = (refreshKey, onItemClick) => {
    const { idTenant, user } = useContext(TenantContext);
    const { isFullAccess, can } = usePermissions();

    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState({
        [TASK_STATUS.SCHEDULED]: { id: TASK_STATUS.SCHEDULED, title: TASK_STATUS_LABELS[TASK_STATUS.SCHEDULED], items: [] },
        [TASK_STATUS.NEW]: { id: TASK_STATUS.NEW, title: TASK_STATUS_LABELS[TASK_STATUS.NEW], items: [] },
        [TASK_STATUS.WAITING]: { id: TASK_STATUS.WAITING, title: TASK_STATUS_LABELS[TASK_STATUS.WAITING], items: [] },
        [TASK_STATUS.OVERDUE]: { id: TASK_STATUS.OVERDUE, title: TASK_STATUS_LABELS[TASK_STATUS.OVERDUE], items: [] },
        [TASK_STATUS.DONE]: { id: TASK_STATUS.DONE, title: TASK_STATUS_LABELS[TASK_STATUS.DONE], items: [] }
    });

    const [dragModal, setDragModal] = useState({
        open: false,
        type: null,      // 'waiting'
        item: null,
        sourceColumn: null,
    });

    const fetchTasks = useCallback(async () => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const [fetchedTasks, employees] = await Promise.all([
                TaskService.getTasks(idTenant),
                EmployeeService.getAllByTenant(idTenant).catch(err => {
                    console.error("Erro ao buscar colaboradores para fotos:", err);
                    return [];
                })
            ]);

            // Mapeia o ID do colaborador (id ou uid) para sua photoUrl atualizada
            const employeePhotoMap = {};
            employees.forEach(emp => {
                const empId = emp.id || emp.uid;
                if (empId && emp.photoUrl) {
                    employeePhotoMap[empId] = emp.photoUrl;
                }
            });

            const newColumns = {
                [TASK_STATUS.SCHEDULED]: { id: TASK_STATUS.SCHEDULED, title: TASK_STATUS_LABELS[TASK_STATUS.SCHEDULED], items: [] },
                [TASK_STATUS.NEW]: { id: TASK_STATUS.NEW, title: TASK_STATUS_LABELS[TASK_STATUS.NEW], items: [] },
                [TASK_STATUS.WAITING]: { id: TASK_STATUS.WAITING, title: TASK_STATUS_LABELS[TASK_STATUS.WAITING], items: [] },
                [TASK_STATUS.OVERDUE]: { id: TASK_STATUS.OVERDUE, title: TASK_STATUS_LABELS[TASK_STATUS.OVERDUE], items: [] },
                [TASK_STATUS.DONE]: { id: TASK_STATUS.DONE, title: TASK_STATUS_LABELS[TASK_STATUS.DONE], items: [] }
            };

            fetchedTasks.forEach(task => {
                // Sobrescreve as fotos desnormalizadas/antigas com as fotos mais recentes dos consultores
                const updatedAssignedEmployees = task.assignedEmployees?.map(emp => {
                    const empId = emp.id || emp.uid;
                    return {
                        ...emp,
                        photoUrl: (empId && employeePhotoMap[empId]) || emp.photoUrl || ""
                    };
                }) || [];
                // Filtro RBAC: Restringe tarefas se o usuário não tiver permissão ampla de visualização
                // Usuários com dashboard gerencial liberado ou os que gerenciam a equipe vêem o board inteiro.
                const canViewAll = isFullAccess || can('dashboards_manager_view') || can('dashboards_financial_view') || can('employees_manage');
                if (!canViewAll) {
                    const isAssigned = task.assignedEmployees?.some(emp => emp.uid === user?.uid || emp.id === user?.uid);
                    const isCreator = task.createdBy?.uid === user?.uid || task.createdBy?.id === user?.uid;
                    if (!isAssigned && !isCreator) {
                        return; // Oculta tarefas que não pertencem ao usuário atual
                    }
                }

                let badgeInfo = { text: 'Tarefa', color: 'primary' };
                if (task.type === TASK_TYPE.CONTACT) badgeInfo = { text: 'Contato', color: 'primary' };
                else if (task.type === TASK_TYPE.TECHNICAL) badgeInfo = { text: 'Avaliação/Treino', color: 'info' };
                else if (task.type === TASK_TYPE.FINANCIAL) badgeInfo = { text: 'Financeiro', color: 'success' };
                else if (task.type === TASK_TYPE.MAINTENANCE) badgeInfo = { text: 'Manutenção', color: 'warning' };
                else if (task.type === TASK_TYPE.INTERNAL) badgeInfo = { text: 'Interno', color: 'secondary' };

                let dateStr = '';
                let isFuture = false;
                if (task.scheduledDate) {
                    dateStr = formatTaskDate(task.scheduledDate, task.scheduledTime);
                    isFuture = isFutureDate(task.scheduledDate);
                }

                let status = task.status || TASK_STATUS.NEW;
                if (status === TASK_STATUS.DOING) {
                    status = TASK_STATUS.WAITING; // retrocompatibilidade
                }

                if (status === 'archived') {
                    return; // Ignore archived tasks
                }

                const item = {
                    id: task.id,
                    type: task.type,
                    title: task.title,
                    description: task.description,
                    date: dateStr,
                    rawDate: task.scheduledDate, // <-- needed for date math in Kanban
                    badgeInfo,
                    ...task,
                    assignedEmployees: updatedAssignedEmployees
                };

                // Move visualmente para "Agendados" tarefas novas mas no futuro
                if (status === TASK_STATUS.NEW && isFuture) {
                    status = TASK_STATUS.SCHEDULED;
                } else if (status === TASK_STATUS.SCHEDULED && !isFuture) {
                    status = TASK_STATUS.NEW;
                }

                if (newColumns[status]) {
                    newColumns[status].items.push(item);
                } else {
                    newColumns[TASK_STATUS.NEW].items.push(item);
                }
            });

            setColumns(newColumns);
        } catch (error) {
            toast.error("Erro ao buscar tarefas");
            console.error("Erro em fetchTasks:", error);
        } finally {
            setLoading(false);
        }
    }, [idTenant, user?.uid, isFullAccess, can]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks, refreshKey]);

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const startColumnId = source.droppableId;
        const finishColumnId = destination.droppableId;

        // Guard 1: Bloqueio de drag de 'done'
        if (startColumnId === TASK_STATUS.DONE) {
            toast.info("Cards concluídos são arquivados automaticamente. Abra o card para mais ações.");
            return; // sem mudança visual — card já está em done
        }

        // Guard 2: Drag para 'done' → abre sidebar com toast
        if (finishColumnId === TASK_STATUS.DONE) {
            const item = columns[startColumnId]?.items.find(i => i.id === draggableId);
            if (item && onItemClick) onItemClick(item, true); // passa shouldScrollToOutcome = true
            toast.info("Para concluir, registre o resultado no painel lateral.");
            return; // sem mudança visual — card permanece na coluna atual
        }

        // Guard 3: Drag para 'waiting' → abre mini-modal
        if (finishColumnId === TASK_STATUS.WAITING) {
            const item = columns[startColumnId]?.items.find(i => i.id === draggableId);
            setDragModal({ open: true, type: 'waiting', item, sourceColumn: startColumnId });
            return; // sem mudança visual até confirmar no modal
        }

        // Drag livre entre demais colunas
        const newColumns = { ...columns };
        const startColumn = newColumns[startColumnId];
        const finishColumn = newColumns[finishColumnId];

        const startItems = Array.from(startColumn.items);
        const [movedItem] = startItems.splice(source.index, 1);

        const finishItems = startColumnId === finishColumnId ? startItems : Array.from(finishColumn.items);
        finishItems.splice(destination.index, 0, movedItem);

        newColumns[startColumnId] = { ...startColumn, items: startItems };
        if (startColumnId !== finishColumnId) {
            newColumns[finishColumnId] = { ...finishColumn, items: finishItems };
        }

        setColumns(newColumns);

        if (startColumnId !== finishColumnId) {
            try {
                // O kanban remapeia visualmente SCHEDULED↔NEW baseado na data futura.
                // Para evitar entradas duplicadas no statusHistory, verificamos o status REAL
                // do banco antes de salvar. Se já for o mesmo, não gravamos.
                const realStatus = movedItem.status; // status original do Firestore

                // Resolve o status real de destino (SCHEDULED é visual, no banco vira NEW)
                let targetStatus = finishColumnId;
                if (finishColumnId === TASK_STATUS.SCHEDULED) {
                    targetStatus = TASK_STATUS.NEW;
                }

                if (realStatus !== targetStatus) {
                    await TaskService.updateTaskStatus(idTenant, user, draggableId, targetStatus);
                }
                // Synchronize seamlessly in background so the clicked card receives the new statusHistory array 
                fetchTasks();
            } catch (error) {
                toast.error("Erro ao atualizar status");
                fetchTasks();
            }
        }
    };

    const handleDragModalConfirm = async ({ outcome, followUpDate }) => {
        const { item } = dragModal;
        setDragModal({ open: false, type: null, item: null, sourceColumn: null });
        try {
            await TaskService.registerOutcome(idTenant, user, item.id, { outcome, followUpDate });
            toast.success("Combinado! Card movido para Aguardando Retorno.");
            fetchTasks();
        } catch (error) {
            toast.error("Erro ao registrar resultado.");
            fetchTasks();
        }
    };

    const handleDragModalCancel = () => {
        setDragModal({ open: false, type: null, item: null, sourceColumn: null });
        // Recarrega para reposicionar o card visualmente se necessário
        fetchTasks();
    };

    return { 
        columns, 
        loading, 
        onDragEnd, 
        refetch: fetchTasks,
        dragModal,
        handleDragModalConfirm,
        handleDragModalCancel
    };
};

